import { Box, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo, useState } from 'react';
import { formatDocument, formatPhone } from '../../common/formatters';
import { Kpi, KpiGrid, formatMoneyShort, parseMoney } from '../common/MobileDashboardKit';
import { KitButton, KitCard, KitItem, KitList, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitAvatar, KitEmpty, KitKV, KitPlate, KitSeg } from '../common/MobileKitExtras';
import { useAppBarAction, useAppBarTitle } from '../layout/AppBarActions';
import { orderStatusView } from '../serviceorder/orderMobileUtils';
import { customerInitials } from './customerMobileUtils';

dayjs.extend(utc);

/**
 * Detalhe do cliente no shell mobile — `prototipos-mobile/clientes/detalhe.html`.
 * Só apresentação: dados, permissões e ações vêm de `pages/customers/View.js`.
 */

const PhoneIcon = protoIcon('phone');
const MsgIcon = protoIcon('msg');
const MailIcon = protoIcon('mail');
const OrdersIcon = protoIcon('orders');
const ReceiptIcon = protoIcon('receipt');
const QuoteIcon = protoIcon('quote');
const CarIcon = protoIcon('car');
const EditIcon = protoIcon('edit');
const DotsIcon = protoIcon('doth');
const TrashIcon = protoIcon('trash');

// `.sheet .action-list .item`
const ACTION_ITEM_SX = { border: 0, borderBottom: 0, p: '12px 4px' };

const TABS = [
    { value: 'h', label: 'Histórico' },
    { value: 'v', label: 'Veículos' },
    { value: 'd', label: 'Dados' },
];

const digits = (text) => String(text || '').replace(/\D/g, '');

// "17/10/2026 14:32" -> dayjs
const parseSaleDate = (text) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(String(text || ''));
    return m ? dayjs(`${m[3]}-${m[2]}-${m[1]}T${m[4] || '00'}:${m[5] || '00'}`) : null;
};

// "hoje" ou "dd/mm" (como "Troca de pastilhas · hoje" / "12/09" do protótipo)
const shortDay = (moment) => {
    if (!moment) return '—';
    return moment.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') ? 'hoje' : moment.format('DD/MM');
};

// Botão de contato (`.btn.soft.sm` só com ícone)
function ContactButton({ icon: Icon, href, label, external }) {
    const enabled = Boolean(href);
    return (
        <KitButton
            variant="soft"
            size="sm"
            component="a"
            type={undefined}
            href={enabled ? href : undefined}
            target={enabled && external ? '_blank' : undefined}
            rel={enabled && external ? 'noopener noreferrer' : undefined}
            aria-label={label}
            aria-disabled={!enabled}
            sx={enabled ? undefined : { opacity: 0.5, pointerEvents: 'none' }}
        >
            <Icon size={16} />
        </KitButton>
    );
}

export default function MobileCustomerDetail({
    loading,
    customer,
    personType,
    displayName,
    docLabel,
    docValue,
    vehicles,
    orderServices,
    sales,
    initialTab,
    onTabChange,
    canUpdate,
    canDelete,
    canCreateOrder,
    canCreateQuote,
    canCreateVehicle,
    canDeleteVehicle,
    onEdit,
    onDelete,
    onGenerateOrder,
    onGenerateQuote,
    onOpenVehicle,
    onDeleteVehicle,
    onAddVehicle,
    onOpenOrder,
    onOpenSale,
}) {
    const [tab, setTab] = useState(initialTab || 'h');
    const [actionsOpen, setActionsOpen] = useState(false);

    const hasActions = canCreateOrder || canCreateQuote || canDelete;

    useAppBarTitle('Cliente');
    useAppBarAction({
        actions: [
            ...(canUpdate ? [{ label: 'Editar', icon: EditIcon, onClick: onEdit, disabled: loading }] : []),
            ...(hasActions ? [{ label: 'Ações', icon: DotsIcon, onClick: () => setActionsOpen(true), disabled: loading }] : []),
        ],
    });

    const history = useMemo(() => {
        const orders = orderServices.map((order) => ({
            kind: 'order',
            key: `o-${order.order_service_id || order.number}`,
            moment: order.created_at ? dayjs.utc(order.created_at) : null,
            order,
        }));
        const saleRows = sales.map((sale) => ({
            kind: 'sale',
            key: `s-${sale.id}`,
            moment: parseSaleDate(sale.created_date),
            sale,
        }));
        return [...orders, ...saleRows].sort((a, b) => (b.moment ? b.moment.valueOf() : 0) - (a.moment ? a.moment.valueOf() : 0));
    }, [orderServices, sales]);

    const totalSpent = useMemo(() => {
        const salesTotal = sales
            .filter((sale) => String(sale.status || '').trim().toLowerCase() === 'finalizada')
            .reduce((sum, sale) => sum + parseMoney(sale.final_total), 0);
        const ordersTotal = orderServices
            .filter((order) => ['finalizada', 'paga', 'faturada'].includes(String(order.status || '').trim().toLowerCase()))
            .reduce((sum, order) => sum + parseMoney(order.amount), 0);
        return salesTotal + ordersTotal;
    }, [sales, orderServices]);

    if (loading || !customer) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={92} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={160} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const handleTab = (value) => {
        setTab(value);
        if (onTabChange) onTabChange(value);
    };

    const phone = customer.cell_phone || customer.phone_number;
    const whatsapp = digits(customer.cell_phone || customer.phone_number);
    const address = [
        [customer.address, customer.number_address].filter(Boolean).join(', '),
        customer.city,
    ].filter(Boolean).join(' · ');
    const birth = customer.birthdate ? `${customer.birthdate}${customer.age ? ` (${customer.age} anos)` : ''}` : '';

    const rows = [
        ['E-mail', customer.email],
        ['Celular', formatPhone(customer.cell_phone)],
        ['Endereço', address],
        ['Telefone', formatPhone(customer.phone_number)],
        ...(personType === 'pj'
            ? [
                ['Razão social', customer.legal_name],
                ['Nome fantasia', customer.trade_name],
                ['Inscrição estadual', customer.state_registration],
                ['Inscrição municipal', customer.municipal_registration],
                ['Atividade', customer.activity_type && customer.activity_type !== 'Selecione' ? customer.activity_type : ''],
                ['CNAE principal', customer.principal_cnae],
            ]
            : [['Nascimento', birth]]),
        ['Complemento', customer.complement],
        ['Bairro', customer.neighborhood],
        ['CEP', customer.cep],
    ].filter(([label, value], index) => (index < 3 ? true : Boolean(value)));

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard sx={{ textAlign: 'center' }}>
                <KitAvatar size={76} text={customerInitials(displayName)} sx={{ m: '0 auto 10px' }} />
                <Box sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.4 }}>{displayName || '—'}</Box>
                <Box sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4 }}>{docLabel} {formatDocument(docValue) || '—'}</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', mt: '12px' }}>
                    <ContactButton icon={PhoneIcon} label="Ligar" href={phone ? `tel:${digits(phone)}` : undefined} />
                    <ContactButton icon={MsgIcon} label="WhatsApp" external href={whatsapp ? `https://wa.me/55${whatsapp}` : undefined} />
                    <ContactButton icon={MailIcon} label="Enviar e-mail" href={customer.email ? `mailto:${customer.email}` : undefined} />
                </Box>
            </KitCard>

            <KpiGrid first>
                <Kpi label="Total gasto" icon="dollar" value={formatMoneyShort(totalSpent)} compact />
                <Kpi label="Ordens" icon="orders" value={String(orderServices.length)} compact />
            </KpiGrid>

            <Box sx={{ mt: '12px' }}>
                <KitSeg options={TABS} value={tab} onChange={handleTab} />
            </Box>

            <Box sx={{ mt: '12px' }}>
                {tab === 'h' && (
                    history.length === 0 ? (
                        <KitEmpty icon={OrdersIcon} title="Nenhum histórico ainda">
                            Ordens de serviço e vendas deste cliente aparecerão aqui.
                        </KitEmpty>
                    ) : (
                        <KitList>
                            {history.map((entry) => {
                                if (entry.kind === 'order') {
                                    const view = orderStatusView(entry.order.status);
                                    return (
                                        <KitItem
                                            key={entry.key}
                                            icon={OrdersIcon}
                                            title={`OS #${entry.order.number}`}
                                            sub={shortDay(entry.moment)}
                                            end={<KitPill tone={view.tone}>{view.label}</KitPill>}
                                            onClick={() => onOpenOrder(entry.order.order_service_id)}
                                        />
                                    );
                                }
                                const day = String(entry.sale.created_date || '').slice(0, 5);
                                return (
                                    <KitItem
                                        key={entry.key}
                                        icon={ReceiptIcon}
                                        tone="green"
                                        title={`Venda #${entry.sale.number}`}
                                        sub={[day, entry.sale.seller].filter(Boolean).join(' · ') || '—'}
                                        end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {entry.sale.final_total}</Box>}
                                        onClick={() => onOpenSale(entry.sale.id)}
                                    />
                                );
                            })}
                        </KitList>
                    )
                )}

                {tab === 'v' && (
                    <>
                        {vehicles.length === 0 ? (
                            <KitEmpty icon={CarIcon} title="Nenhum veículo cadastrado">
                                Adicione o primeiro veículo deste cliente para vincular ordens de serviço a ele.
                            </KitEmpty>
                        ) : (
                            <KitList>
                                {vehicles.map((vehicle) => {
                                    const years = vehicle.ModYear || vehicle.FabYear || '';
                                    const title = [vehicle.Manufacturer, vehicle.Model, years].filter(Boolean).join(' ') || '—';
                                    return (
                                        <KitItem
                                            key={vehicle.ID}
                                            icon={CarIcon}
                                            title={title}
                                            sub={vehicle.LicensePlate ? <KitPlate>{vehicle.LicensePlate}</KitPlate> : (vehicle.Chassi || '—')}
                                            end={canDeleteVehicle ? (
                                                <Box
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Remover veículo"
                                                    onClick={(e) => { e.stopPropagation(); onDeleteVehicle(vehicle); }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onDeleteVehicle(vehicle); } }}
                                                    sx={(theme) => ({ width: 40, height: 40, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.palette.error.main, cursor: 'pointer' })}
                                                >
                                                    <TrashIcon size={20} />
                                                </Box>
                                            ) : undefined}
                                            chevron
                                            onClick={() => onOpenVehicle(vehicle.ID)}
                                        />
                                    );
                                })}
                            </KitList>
                        )}
                        {canCreateVehicle && (
                            <KitButton variant="soft" block sx={{ mt: '12px' }} onClick={onAddVehicle}>Adicionar veículo</KitButton>
                        )}
                    </>
                )}

                {tab === 'd' && (
                    <KitCard>
                        {rows.map(([label, value]) => <KitKV key={label} label={label} value={value || '—'} />)}
                    </KitCard>
                )}
            </Box>

            <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Ações" actionList>
                <Box>
                    {canCreateOrder && (
                        <KitItem icon={OrdersIcon} title="Gerar OS" sub="" chevron sx={ACTION_ITEM_SX} onClick={() => { setActionsOpen(false); onGenerateOrder(); }} />
                    )}
                    {canCreateQuote && (
                        <KitItem icon={QuoteIcon} title="Gerar orçamento" sub="" chevron sx={ACTION_ITEM_SX} onClick={() => { setActionsOpen(false); onGenerateQuote(); }} />
                    )}
                    {canDelete && (
                        <KitItem icon={TrashIcon} tone="red" title="Excluir cliente" sub="" chevron sx={ACTION_ITEM_SX} onClick={() => { setActionsOpen(false); onDelete(); }} />
                    )}
                </Box>
            </MobileSheet>
        </Box>
    );
}
